// DeliverablesCard — delivery cadence health for deliverables and
// obligations.
//
// The population is LEAF OCCURRENCES, not recurring parent templates: a
// monthly report obligation is twelve things to deliver, not one, and
// counting the parent would understate both the workload and the misses. The
// card says so explicitly so the reader is not left to guess the unit.

import MetricCard from "../tracking/deliverable/MetricCard";
import ReportSection, {
  NothingToReport,
  UnavailableNotice,
} from "./ReportSection";
import { countTone, formatPercent, onTimeRateTone } from "./reportFormat";
import type {
  SectionUnavailable,
  SummaryDeliverablesSection,
} from "../../../types/report";

const SECTION_LABEL = "Deliverables and obligations";

export interface DeliverablesCardProps {
  section: SummaryDeliverablesSection | SectionUnavailable;
}

export default function DeliverablesCard({ section }: DeliverablesCardProps) {
  if (section.status === "unavailable") {
    return (
      <ReportSection id="deliverables" title={SECTION_LABEL}>
        <UnavailableNotice label={SECTION_LABEL} />
      </ReportSection>
    );
  }

  if (section.leafCount === 0) {
    return (
      <ReportSection id="deliverables" title={SECTION_LABEL}>
        <NothingToReport />
      </ReportSection>
    );
  }

  return (
    <ReportSection id="deliverables" title={SECTION_LABEL}>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <MetricCard
          testId="deliverables-on-time"
          label="Delivered on time"
          value={formatPercent(section.onTimeRate)}
          valueTone={onTimeRateTone(section.onTimeRate)}
          subtitle={`${section.onTimeCount} of ${section.totalResolved} resolved`}
        />
        <MetricCard
          testId="deliverables-overdue"
          label="Overdue"
          value={section.overdueCount}
          valueTone={countTone(section.overdueCount, "danger")}
        />
        <MetricCard
          testId="deliverables-awaiting-review"
          label="Awaiting customer review"
          value={section.awaitingReviewCount}
          valueTone={countTone(section.awaitingReviewCount, "warning")}
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <p
          data-testid="deliverables-rag-flags"
          className="flex flex-wrap items-center gap-3 text-caption text-slate-500 dark:text-slate-400"
        >
          <RagFlag
            label="Green"
            count={section.ragFlags.green}
            dotClass="bg-emerald-500"
          />
          <RagFlag
            label="Amber"
            count={section.ragFlags.amber}
            dotClass="bg-amber-500"
          />
          <RagFlag
            label="Red"
            count={section.ragFlags.red}
            dotClass="bg-red-500"
          />
        </p>
        <p
          data-testid="deliverables-leaf-count"
          className="text-caption text-slate-500 dark:text-slate-400"
        >
          {section.leafCount} deliverable occurrences
        </p>
      </div>
    </ReportSection>
  );
}

function RagFlag({
  label,
  count,
  dotClass,
}: {
  label: string;
  count: number;
  dotClass: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 tabular-nums">
      <span
        aria-hidden="true"
        className={`h-2 w-2 rounded-full ${dotClass}`}
      />
      {label} {count}
    </span>
  );
}
